import React, { FC, useEffect, useRef, useState } from "react";
import { useQueries } from "react-query";
import axios from "axios";
import classNames from "classnames";
import classes from "./BranchQueueFilter.module.scss";
import { useGetBranches } from "../../../hooks/Admin/useGetBranches";
import { handleClickOutside } from "../../../utils/Admin/handleClickOutside";
import { BranchAPI } from "../../../constants/API";
import { commonHeaders } from "../../../constants/HeadersAPI";
import type { BranchQueueItemDto } from "../../../hooks/Admin/WorkingPlacesTable/useGetQueuesForCurrentUser";

export type BranchQueueFilterProps = {
  selectedBranchIds: number[];
  selectedQueueIds: number[];
  onBranchChange: (ids: number[]) => void;
  onQueueChange: (ids: number[]) => void;
  /** Если true, можно выбрать только одну очередь (настройки одной очереди). */
  singleQueue?: boolean;
  /** Компактный вариант для плотных шапок. */
  compact?: boolean;
  /** Если задан, отображаются только филиалы с этими id (например, для администратора филиала). */
  branchIdsFilter?: number[];
  /** Если задан, отображаются только очереди с этими id (например, для администратора очереди). */
  queueIdsFilter?: number[];
};

const BranchQueueFilter: FC<BranchQueueFilterProps> = ({
  selectedBranchIds,
  selectedQueueIds,
  onBranchChange,
  onQueueChange,
  singleQueue = false,
  compact = false,
  branchIdsFilter,
  queueIdsFilter,
}) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const { branches, isLoading: loadingBranches } = useGetBranches();

  const filteredBranches = branchIdsFilter?.length
    ? branches.filter((b) => branchIdsFilter.includes(b.id))
    : branches;

  const effectiveQueueIdsFilter = queueIdsFilter?.length ? queueIdsFilter : undefined;

  const queueResults = useQueries(
    filteredBranches.map((b) => ({
      queryKey: ["branches", b.id, "queues"] as const,
      queryFn: () =>
        axios
          .get<BranchQueueItemDto[]>(BranchAPI.getQueues(b.id), commonHeaders())
          .then((r) => r.data),
      enabled: open && !!b.id,
    })),
  );

  const branchesWithQueues = filteredBranches
    .map((b, i) => {
      const rawQueues = queueResults[i]?.data ?? [];
      const filteredQueues = effectiveQueueIdsFilter
        ? rawQueues.filter((q) => effectiveQueueIdsFilter.includes(q.id))
        : rawQueues;
      return {
        branch: b,
        queues: filteredQueues,
        isLoadingQueues: queueResults[i]?.isLoading ?? false,
      };
    })
    .filter(({ queues, isLoadingQueues }) => {
      if (!effectiveQueueIdsFilter) return true;
      if (isLoadingQueues) return true;
      return queues.length > 0;
    });

  useEffect(() => {
    const close = () => setOpen(false);
    const handler = (e: MouseEvent) =>
      handleClickOutside(e, panelRef, close as (id: string | null) => void);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleBranch = (branchId: number, queueIdsInBranch: number[]) => {
    if (queueIdsInBranch.length === 0) return;

    const selectedCountInBranch = queueIdsInBranch.filter((id) =>
      selectedQueueIds.includes(id),
    ).length;

    const isAllSelectedInBranch =
      selectedCountInBranch === queueIdsInBranch.length;

    // Включение филиала => выбираем ВСЕ очереди филиала.
    if (!isAllSelectedInBranch) {
      if (singleQueue) {
        onQueueChange([queueIdsInBranch[0]]);
        onBranchChange([branchId]);
      } else {
        onQueueChange(
          Array.from(new Set([...selectedQueueIds, ...queueIdsInBranch])),
        );
        if (!selectedBranchIds.includes(branchId)) {
          onBranchChange([...selectedBranchIds, branchId]);
        }
      }
      return;
    }

    // Выключение филиала => снимаем ВЕСЬ выбор с очередей филиала.
    if (singleQueue) {
      onQueueChange([]);
      onBranchChange([]);
    } else {
      onQueueChange(
        selectedQueueIds.filter((id) => !queueIdsInBranch.includes(id)),
      );
      onBranchChange(selectedBranchIds.filter((id) => id !== branchId));
    }
  };

  const toggleQueue = (
    queueId: number,
    branchId: number,
    queueIdsInBranch: number[],
  ) => {
    if (selectedQueueIds.includes(queueId)) {
      const nextQueueIds = selectedQueueIds.filter((x) => x !== queueId);
      onQueueChange(nextQueueIds);
      if (!singleQueue) {
        const hasOtherSelectedInBranch = queueIdsInBranch.some(
          (id) => id !== queueId && nextQueueIds.includes(id),
        );
        if (!hasOtherSelectedInBranch && selectedBranchIds.includes(branchId)) {
          onBranchChange(selectedBranchIds.filter((x) => x !== branchId));
        }
      } else {
        onBranchChange([]);
      }
    } else {
      if (singleQueue) {
        onQueueChange([queueId]);
        onBranchChange([branchId]);
      } else {
        onQueueChange([...selectedQueueIds, queueId]);
        if (!selectedBranchIds.includes(branchId)) {
          onBranchChange([...selectedBranchIds, branchId]);
        }
      }
    }
  };

  const label =
    singleQueue && selectedQueueIds.length > 0
      ? `Очередь: ${selectedQueueIds.length}`
      : selectedBranchIds.length > 0 || selectedQueueIds.length > 0
        ? `Филиалы: ${selectedBranchIds.length}, Очереди: ${selectedQueueIds.length}`
        : "Все филиалы и очереди";

  return (
    <div className={classes.wrap} ref={panelRef}>
      <button
        type="button"
        className={classNames(
          classes.trigger,
          { [classes.open]: open },
          { [classes.compact]: compact },
        )}
        onClick={() => setOpen(!open)}
      >
        <span className={classes.triggerLabel}>Выбрано</span>
        <span className={classes.triggerValue}>{label}</span>
        <span className={classes.arrow}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className={classes.panel}>
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Филиалы и очереди</div>
            {loadingBranches ? (
              <div className={classes.hint}>Загрузка...</div>
            ) : branchesWithQueues.length === 0 ? (
              <div className={classes.hint}>Нет доступных филиалов</div>
            ) : (
              <div className={classes.treeList}>
                {branchesWithQueues.map(
                  ({ branch: b, queues, isLoadingQueues }) => (
                    <div key={b.id} className={classes.branchGroup}>
                      <div className={classes.branchItem}>
                        {(() => {
                          const queueIdsInBranch = queues.map((q) => q.id);
                          const selectedCountInBranch = queueIdsInBranch.filter(
                            (id) => selectedQueueIds.includes(id),
                          ).length;

                          const isAllSelectedInBranch =
                            queueIdsInBranch.length > 0 &&
                            selectedCountInBranch === queueIdsInBranch.length;

                          const isIndeterminateInBranch =
                            queueIdsInBranch.length > 0 &&
                            selectedCountInBranch > 0 &&
                            selectedCountInBranch < queueIdsInBranch.length;

                          return (
                            <input
                              type="checkbox"
                              checked={isAllSelectedInBranch}
                              ref={(el) => {
                                if (el)
                                  el.indeterminate = isIndeterminateInBranch;
                              }}
                              disabled={queueIdsInBranch.length === 0}
                              onChange={() =>
                                toggleBranch(b.id, queueIdsInBranch)
                              }
                            />
                          );
                        })()}
                        <span>{b.name || b.code || `Филиал ${b.id}`}</span>
                      </div>
                      <div className={classes.queueSubList}>
                        {isLoadingQueues ? (
                          <div className={classes.hint}>
                            Загрузка очередей...
                          </div>
                        ) : queues.length === 0 ? (
                          <div className={classes.queueEmpty}>Нет очередей</div>
                        ) : (
                          queues.map((q) => (
                            <label key={q.id} className={classes.queueItem}>
                              <input
                                type="checkbox"
                                checked={selectedQueueIds.includes(q.id)}
                                onChange={() =>
                                  toggleQueue(
                                    q.id,
                                    b.id,
                                    queues.map((qu) => qu.id),
                                  )
                                }
                              />
                              <span>{q.queueName || `Очередь ${q.id}`}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
          <div className={classes.footer}>
            <button
              type="button"
              className={classes.resetBtn}
              onClick={() => {
                onBranchChange([]);
                onQueueChange([]);
              }}
            >
              Сбросить
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchQueueFilter;
